import { redirect, LoaderFunction, LoaderFunctionArgs } from 'react-router-dom'
import { useSessionStore } from '@/stores'
import { isInteractivePreviewMode } from '@/utils/previewMode'
import { isDrawerOwningFunnel } from './utils/portalSession'
import { isSyncedToUrl, resyncPortalSession } from './utils/portalRecovery'

/**
 * This portal's own route guards. They live here rather than in `src/router/guards.ts`
 * because they encode portal-specific rules — the customer-portal campaign flag, the
 * `/portal/login` fallback, and this portal's mid-funnel recovery. A shared guard that
 * knew any of that would have to be edited every time a portal is added.
 */

const requirePortalSession = (): void => {
  const sessionStore = useSessionStore.getState()
  if (!sessionStore.isSessionValid() || !sessionStore.isCustomerPortal()) {
    throw redirect('/')
  }
}

// Authenticated portal pages — additionally require a matched + verified customer;
// otherwise send the user to the portal login (match) page.
export const portalAuthedGuardLoader: LoaderFunction = async ({ request }: LoaderFunctionArgs) => {
  requirePortalSession()

  if (!useSessionStore.getState().isSessionUserKnown()) {
    throw redirect('/portal/login')
  }

  // A reload rehydrates a mid-funnel step; walk it out before rendering. The
  // preview has no server step to resync, and this is the guard's only API call.
  if (!isInteractivePreviewMode() && !isDrawerOwningFunnel()) {
    const { pathname } = new URL(request.url)
    if (!isSyncedToUrl(pathname)) {
      await resyncPortalSession(pathname)
    }
  }

  return null
}

// Portal login page — only for un-authenticated portal sessions. If the customer is
// already known, skip straight to the portal home.
export const portalLoginGuardLoader: LoaderFunction = async () => {
  requirePortalSession()

  if (useSessionStore.getState().isSessionUserKnown()) {
    throw redirect('/portal')
  }

  return null
}
